/**
 * Safe Integrated Asset Manager Component
 *
 * Provides interface for selecting and managing Safe assets with quantum-safe encryption
 * that requires Safe owner consensus through multi-signature approval workflow.
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Checkbox,
  Chip,
  LinearProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  AccountBalance as EthIcon,
  Token as TokenIcon,
  Collections as NftIcon,
  Lock as LockIcon,
  Refresh as RefreshIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import type { SafeInfo } from '@safe-global/safe-apps-sdk';
import type { QuantumKeyManager } from '@/crypto/KeyManager';
import type { NISTQuantumSafe } from '@/crypto/NISTQuantumSafe';
import type { SecurityLevel } from '@/types/quantum';
import { createSafeAssetService } from '@/services/SafeAssetService';
import type { SafeAsset } from '@/services/SafeAssetService';
import { getEncryptedAssetStorage } from '@/services/EncryptedAssetStorage';
import { SafeQuantumIntegration, createSafeQuantumIntegration } from '@/services/SafeQuantumIntegration';
import type { QuantumVaultProposal } from '@/services/SafeQuantumIntegration';
import { SafeQuantumProposalManager } from './SafeQuantumProposalManager';
import { useSafeSDK } from '@/hooks/useSafeSDK';
import { ethers } from 'ethers';

interface Asset extends SafeAsset {
  isEncrypted: boolean;
  encryptionTimestamp?: number;
  isSelected?: boolean;
}

interface SafeIntegratedAssetManagerProps {
  keyManager: QuantumKeyManager;
  quantumSafe: NISTQuantumSafe;
  isConnected: boolean;
}

interface QuantumProposalDialogProps {
  open: boolean;
  onClose: () => void;
  selectedAssets: Asset[];
  onSubmitProposal: (assets: SafeAsset[], securityLevel: SecurityLevel, description: string) => Promise<void>;
  loading: boolean;
}

const QuantumProposalDialog: React.FC<QuantumProposalDialogProps> = ({
  open,
  onClose,
  selectedAssets,
  onSubmitProposal,
  loading
}) => {
  const [description, setDescription] = useState('');
  const [securityLevel, setSecurityLevel] = useState<SecurityLevel>(5);
  const [step, setStep] = useState(0);

  const handleSubmit = async () => {
    setStep(1);
    try {
      await onSubmitProposal(selectedAssets, securityLevel, description);
      setStep(2);
      setTimeout(() => {
        onClose();
        setStep(0);
        setDescription('');
      }, 2000);
    } catch (error) {
      setStep(0);
    }
  };


  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon />
          Create Quantum Encryption Proposal
          <Chip label={`${selectedAssets.length} assets`} size="small" />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} orientation="vertical" sx={{ mt: 2 }}>
          <Step>
            <StepLabel>Configure Encryption Parameters</StepLabel>
            <StepContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                {/* Security Level Selection */}
                <Box>
                  <Typography variant="h6" gutterBottom>Security Level</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant={securityLevel === 3 ? 'contained' : 'outlined'}
                      onClick={() => setSecurityLevel(3)}
                      size="large"
                    >
                      Category 3 - AES-192 Equivalent
                    </Button>
                    <Button
                      variant={securityLevel === 5 ? 'contained' : 'outlined'}
                      onClick={() => setSecurityLevel(5)}
                      size="large"
                      color="primary"
                    >
                      Category 5 - AES-256 Equivalent (Recommended)
                    </Button>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Category 5 provides stronger quantum resistance but requires more computation.
                  </Typography>
                </Box>

                {/* Algorithm Display */}
                <Box>
                  <Typography variant="h6" gutterBottom>NIST Algorithms</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {(securityLevel === 5
                      ? ['ML-KEM-1024', 'ML-DSA-87', 'SLH-DSA-SHA2-256f']
                      : ['ML-KEM-768', 'ML-DSA-65', 'SLH-DSA-SHA2-192f']
                    ).map(alg => (
                      <Chip key={alg} label={alg} variant="outlined" />
                    ))}
                  </Box>
                </Box>

                {/* Description */}
                <TextField
                  label="Proposal Description"
                  multiline
                  rows={3}
                  fullWidth
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={`Quantum encrypt ${selectedAssets.length} asset(s) with Security Level ${securityLevel}`}
                  helperText="Describe the purpose of this quantum encryption operation for other Safe owners"
                />

                {/* Selected Assets Preview */}
                <Box>
                  <Typography variant="h6" gutterBottom>Selected Assets ({selectedAssets.length})</Typography>
                  <List dense>
                    {selectedAssets.slice(0, 5).map((asset, index) => (
                      <ListItem key={index}>
                        <ListItemIcon>
                          {asset.type === 'ETH' ? <EthIcon /> :
                           asset.type === 'ERC20' ? <TokenIcon /> : <NftIcon />}
                        </ListItemIcon>
                        <ListItemText
                          primary={asset.name || asset.symbol}
                          secondary={`${asset.balance} ${asset.symbol} • ${asset.type}`}
                        />
                      </ListItem>
                    ))}
                    {selectedAssets.length > 5 && (
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        ... and {selectedAssets.length - 5} more assets
                      </Typography>
                    )}
                  </List>
                </Box>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Creating Safe Proposal</StepLabel>
            <StepContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <LinearProgress sx={{ flex: 1 }} />
                <Typography>Submitting to Safe Transaction Service...</Typography>
              </Box>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Proposal Created Successfully</StepLabel>
            <StepContent>
              <Alert severity="success" sx={{ mt: 1 }}>
                Quantum encryption proposal created! Other Safe owners can now review and approve the operation.
              </Alert>
            </StepContent>
          </Step>
        </Stepper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {step === 2 ? 'Close' : 'Cancel'}
        </Button>
        {step === 0 && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || selectedAssets.length === 0}
          >
            Create Proposal
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export const SafeIntegratedAssetManager: React.FC<SafeIntegratedAssetManagerProps> = ({
  keyManager,
  quantumSafe,
  isConnected
}) => {
  const safeSDK = useSafeSDK();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proposalDialogOpen, setProposalDialogOpen] = useState(false);
  const [quantumIntegration, setQuantumIntegration] = useState<SafeQuantumIntegration | null>(null);
  const [currentOwner, setCurrentOwner] = useState<string>('');
  const [activeTab, setActiveTab] = useState(0);
  const [proposals, setProposals] = useState<QuantumVaultProposal[]>([]);

  // Initialize Safe quantum integration
  useEffect(() => {
    if (isConnected && safeSDK.isReady) {
      initializeQuantumIntegration();
      loadAssets();
    }
  }, [isConnected, safeSDK.isReady]);

  const initializeQuantumIntegration = async () => {
    try {
      if (!safeSDK.isReady) {
        console.log('Safe SDK not ready yet, waiting...');
        return;
      }

      // Check if we're in Safe App mode
      const isSafeApp = !!safeSDK.safeInfo;
      console.log('Running in Safe App mode:', isSafeApp);

      let signerAddress: string;
      if (isSafeApp) {
        // In Safe App mode, we can't reliably get the connected owner EOA
        // The provider.getSigner().getAddress() returns the Safe address, not owner
        // Use first owner as a reasonable default for UI purposes
        signerAddress = safeSDK.safeInfo!.owners[0];
        console.log('Safe App mode: Using first owner for UI:', signerAddress);
        console.log('All Safe owners:', safeSDK.safeInfo!.owners);
      } else {
        // Traditional dApp mode - can get actual connected wallet
        if (safeSDK.signer) {
          signerAddress = await safeSDK.signer.getAddress();
        } else if (safeSDK.provider && 'getSigner' in safeSDK.provider) {
          const signer = await (safeSDK.provider as any).getSigner();
          signerAddress = await signer.getAddress();
        } else {
          throw new Error('No signer available in dApp mode');
        }
        console.log('dApp mode: Using connected wallet:', signerAddress);
      }
      setCurrentOwner(signerAddress);

      const integration = createSafeQuantumIntegration(
        safeSDK,
        keyManager,
        quantumSafe
      );
      setQuantumIntegration(integration);

      // Load any existing proposals
      const pendingProposals = integration.getPendingProposals();
      setProposals(pendingProposals);
    } catch (error) {
      console.error('Failed to initialize quantum integration:', error);
      setError(`Failed to initialize Safe integration: ${error}`);
    }
  };

  const loadAssets = async () => {
    if (!safeSDK.safeInfo || !isConnected) return;

    try {
      setIsLoading(true);
      setError(null);

      const assetService = createSafeAssetService(safeSDK.safeInfo, safeSDK.provider || undefined);
      const safeAssets = await assetService.fetchSafeAssets(safeSDK.safeInfo.safeAddress);

      // Check which assets are encrypted
      const encryptedStorage = getEncryptedAssetStorage();
      const encryptedAssets = await encryptedStorage.getEncryptedAssetsForSafe(safeSDK.safeInfo.safeAddress);
      const encryptedAssetIds = new Set(encryptedAssets.map(ea => ea.assetId));

      const assetsWithEncryptionStatus: Asset[] = safeAssets.map(asset => ({
        ...asset,
        isEncrypted: encryptedAssetIds.has(asset.id),
        isSelected: false
      }));

      setAssets(assetsWithEncryptionStatus);
    } catch (error) {
      console.error('Failed to load assets:', error);
      setError(`Failed to load assets: ${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssetSelection = (asset: Asset, selected: boolean) => {
    setAssets(prev => prev.map(a =>
      a.id === asset.id ? { ...a, isSelected: selected } : a
    ));

    if (selected) {
      setSelectedAssets(prev => [...prev, asset]);
    } else {
      setSelectedAssets(prev => prev.filter(a => a.id !== asset.id));
    }
  };

  const handleSelectAll = (selected: boolean) => {
    const selectableAssets = assets.filter(a => !a.isEncrypted);

    setAssets(prev => prev.map(a => ({
      ...a,
      isSelected: !a.isEncrypted && selected
    })));

    setSelectedAssets(selected ? selectableAssets : []);
  };

  const handleCreateProposal = async (
    selectedAssets: SafeAsset[],
    securityLevel: SecurityLevel,
    description: string
  ) => {
    if (!quantumIntegration) throw new Error('Quantum integration not initialized');

    try {
      const proposal = await quantumIntegration.proposeAssetEncryption(
        selectedAssets,
        securityLevel,
        description
      );

      // Update proposals list
      setProposals(prev => [proposal, ...prev]);

      // Clear selections
      setSelectedAssets([]);
      setAssets(prev => prev.map(a => ({ ...a, isSelected: false })));

      console.log('Quantum encryption proposal created:', proposal.id);
    } catch (error) {
      console.error('Failed to create proposal:', error);
      throw error;
    }
  };

  const handleProposalUpdate = (updatedProposal: QuantumVaultProposal) => {
    setProposals(prev => prev.map(p =>
      p.id === updatedProposal.id ? updatedProposal : p
    ));

    // If proposal was executed, refresh assets to show encryption status
    if (updatedProposal.status === 'executed') {
      loadAssets();
    }
  };

  const getAssetIcon = (type: string) => {
    switch (type) {
      case 'ETH': return <EthIcon />;
      case 'ERC20': return <TokenIcon />;
      case 'ERC721': return <NftIcon />;
      default: return <TokenIcon />;
    }
  };

  const selectableAssets = assets.filter(a => !a.isEncrypted);
  const encryptedAssets = assets.filter(a => a.isEncrypted);
  const pendingProposals = proposals.filter(p => p.status === 'pending');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header with Safe Info */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'between' }}>
            <Typography variant="h6">
              <GroupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Safe Quantum Asset Management
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                label={`${safeSDK.safeInfo?.threshold}/${safeSDK.safeInfo?.owners.length} multi-sig`}
                size="small"
                variant="outlined"
                icon={<SecurityIcon />}
              />
              <Chip
                label={safeSDK.safeInfo?.owners.includes(currentOwner) ? 'Owner' : 'Observer'}
                size="small"
                color={safeSDK.safeInfo?.owners.includes(currentOwner) ? 'success' : 'default'}
              />
              <Button
                startIcon={<RefreshIcon />}
                onClick={loadAssets}
                disabled={isLoading}
                size="small"
              >
                Refresh
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs for Assets and Proposals */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Assets ({assets.length})
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Badge badgeContent={pendingProposals.length} color="warning">
                    Proposals
                  </Badge>
                </Box>
              }
            />
          </Tabs>
        </Box>

        {/* Assets Tab */}
        {activeTab === 0 && (
          <CardContent>
            {isLoading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading Safe assets...
                </Typography>
              </Box>
            ) : assets.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <SecurityIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No Assets Found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  This Safe doesn't have any assets to encrypt
                </Typography>
              </Box>
            ) : (
              <>
                {/* Selection Controls */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedAssets.length > 0 && selectedAssets.length === selectableAssets.length}
                          indeterminate={selectedAssets.length > 0 && selectedAssets.length < selectableAssets.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          disabled={selectableAssets.length === 0}
                        />
                      }
                      label={`Select All Available (${selectableAssets.length})`}
                    />
                    {selectedAssets.length > 0 && (
                      <Chip
                        label={`${selectedAssets.length} selected`}
                        color="primary"
                        size="small"
                      />
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<LockIcon />}
                    onClick={() => {
                      console.log('DEBUG: Button clicked');
                      console.log('selectedAssets.length:', selectedAssets.length);
                      console.log('currentOwner:', currentOwner);
                      console.log('safeSDK.safeInfo?.owners:', safeSDK.safeInfo?.owners);
                      console.log('owners includes currentOwner:', safeSDK.safeInfo?.owners.includes(currentOwner));
                      setProposalDialogOpen(true);
                    }}
                    disabled={selectedAssets.length === 0 || !safeSDK.safeInfo?.owners.includes(currentOwner)}
                  >
                    Create Encryption Proposal
                    {/* Debug info */}
                    {selectedAssets.length === 0 && <span style={{fontSize: '10px'}}> (No assets)</span>}
                    {selectedAssets.length > 0 && !safeSDK.safeInfo?.owners.includes(currentOwner) &&
                      <span style={{fontSize: '10px'}}> (Not owner)</span>}
                  </Button>
                </Box>

                {/* Assets List */}
                <List>
                  {/* Available Assets */}
                  {selectableAssets.length > 0 && (
                    <>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Available Assets ({selectableAssets.length})
                      </Typography>
                      {selectableAssets.map((asset) => (
                        <ListItem key={asset.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}>
                          <ListItemIcon>
                            <Checkbox
                              checked={asset.isSelected || false}
                              onChange={(e) => handleAssetSelection(asset, e.target.checked)}
                            />
                          </ListItemIcon>
                          <ListItemIcon>
                            {getAssetIcon(asset.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2">{asset.name || asset.symbol}</Typography>
                                <Chip label={asset.type} size="small" variant="outlined" />
                              </Box>
                            }
                            secondary={`${asset.balance} ${asset.symbol} • ${asset.type}`}
                          />
                          <ListItemSecondaryAction>
                            <Typography variant="body2" color="text.secondary">
                              Ready for encryption
                            </Typography>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </>
                  )}

                  {/* Encrypted Assets */}
                  {encryptedAssets.length > 0 && (
                    <>
                      {selectableAssets.length > 0 && <Divider sx={{ my: 2 }} />}
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                        Quantum Encrypted Assets ({encryptedAssets.length})
                      </Typography>
                      {encryptedAssets.map((asset) => (
                        <ListItem key={asset.id} sx={{ border: 1, borderColor: 'success.main', borderRadius: 1, mb: 1, bgcolor: 'success.50' }}>
                          <ListItemIcon>
                            <LockIcon color="success" />
                          </ListItemIcon>
                          <ListItemIcon>
                            {getAssetIcon(asset.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="subtitle2">{asset.name || asset.symbol}</Typography>
                                <Chip label={asset.type} size="small" variant="outlined" />
                                <Chip label="ENCRYPTED" size="small" color="success" />
                              </Box>
                            }
                            secondary={`Quantum-safe encrypted • ${asset.type}`}
                          />
                          <ListItemSecondaryAction>
                            <Typography variant="body2" color="success.main">
                              Protected by NIST algorithms
                            </Typography>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </>
                  )}
                </List>
              </>
            )}
          </CardContent>
        )}

        {/* Proposals Tab */}
        {activeTab === 1 && quantumIntegration && (
          <CardContent>
            <SafeQuantumProposalManager
              safeInfo={safeSDK.safeInfo!}
              quantumIntegration={quantumIntegration}
              currentOwner={currentOwner}
              onProposalUpdate={handleProposalUpdate}
            />
          </CardContent>
        )}
      </Card>

      {/* Proposal Creation Dialog */}
      <QuantumProposalDialog
        open={proposalDialogOpen}
        onClose={() => setProposalDialogOpen(false)}
        selectedAssets={selectedAssets}
        onSubmitProposal={handleCreateProposal}
        loading={false}
      />
    </Box>
  );
};

export default SafeIntegratedAssetManager;